import { logger } from "@/utils/logger";
import { ActionContext, Module } from "vuex";
import {
  ActionType,
  Address,
  businessDataUtils,
  InventoryStatusEnum,
  Task,
  TaskDefinition,
  TaskModule,
  TaskState
} from "@/types";
import {
  abandonTask,
  buildReserveOpinion,
  executeAction,
  executeOrderAction,
  getGroupsByUserId,
  getRoles,
  getTasks,
  getWaitTask,
  saveOfferFromTask,
  takeChargeTask,
  setNbrReceivedDocuments,
  updateReserveStatus
} from "@/store/services/task/taskService";
import decisionModule from "@/store/modules/task/actions/decisionModule";
import analysisModule from "@/store/modules/task/actions/analysisModule"
import uploadDocumentModule from "@/store/modules/task/actions/uploadDocumentModule";
import Notify from 'quasar/src/plugins/Notify.js';;
import { getOffer, checkOfferStatus } from "@/store/services/offerService";
import partyModule from "@/store/modules/task/actions/partyModule";
import financingRequestModule from "@/store/modules/task/actions/financingRequestModule";
import { deepClone, DownloadAllDocuments, EntityValidations, getEntity, getOfferInfo, getSalesPerson, globalConfig, Masks, parseTaskVariables, Paths, SendAllDocuments, taskVariables, UON, validateOrganizationMandatoryFields } from "@/utils";
import validateDocumentsModule from "@/store/modules/task/actions/validateDocumentsModule";
import printOfferModule from "@/store/modules/task/actions/printOfferModule";
import store from "@/store";
import router from "@/router";
import i18n from "@/i18n";
import modifyConditionsModule from "./actions/modifyConditionsModule";
import { getOrderEditMode, saveOrder } from "@/store/services/order/orderService";
import { getTeamMembersByManagerCode } from "@/components/task/ts";
import { assignTask, sendMessage, unClaimTask } from "@/store/services/task/workflow";
import startOrderModule from "@/store/modules/task/actions/order/startOrderModule";
import validationOrderModule from "@/store/modules/task/actions/order/validationOrderModule";
import { setListDecision } from "@/store/services/middle/middleOfficeService";
import { getCheckPointsGathering, getDocumentValueConfig, getGatheringProcessFromOrder, saveCheckPointsValue, updateDocumentValue, updatefieldValue } from "@/store/services/task/actions/order/completePreconditionsService";
import {
  getGatheringProcessFromOffer,
  updateCheckPointsStatus
} from "@/store/services/task/actions/completePreconditionsService";
import { getProcess } from "@/store/services/task/actions/uploadDocumentService";
import { faBullseye } from "@fortawesome/free-solid-svg-icons";
import printDocumentModule from "@/store/modules/document/printDocumentModule";
import {
  getSelectedDocumentsCountForSend,
  MAX_TRANSMIT_DOCUMENTS,
} from "@/store/services/task/actions/printDocumentService";
import { getTaskOfferReference, navigateToTaskDetail } from "@/utils/taskNavigation";

const TASK_DETAIL_INITIALIZE_VARIABLES =
  "offerId;offerStatus;offerReference;customerName;_offerDecisionProcessId;_clientDecisionProcessId;associated_networks_id;customerRessourceUid;customerBirthDate;offerCreationDate;associated_network_nodes_id;customerType;offerPhase;customerReference;applicationName;analysisProcessId;_decisionAnalysisProcessId;offer;_checkPointsId;additional_action_items;SignatureMethod;proposalReference;_currentOpinionMakersList;_currentOpinionMaker;_decisionMaker;conformityComment;conformityOpinion;_finalDelegationLvl;_calculatedDelegationLvl;updatedDelegationLvlProcess;_jsonDelegation;signatureType;transactionId";

/** Ignore stale responses when the user opens another task before the previous load finishes. */
let taskDetailLoadSeq = 0;

function isStaleTaskDetailLoad(seq: number): boolean {
  return seq !== taskDetailLoadSeq;
}

function readTaskVariableValue(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  if (value && typeof value === "object" && "value" in value) {
    const inner = (value as { value?: unknown }).value;
    if (typeof inner === "string" && inner.length > 0) {
      return inner;
    }
  }
  return undefined;
}

function getIncomingOfferId(task: Task | undefined): string | undefined {
  const vars = task?.variables as Record<string, unknown> | undefined;
  return readTaskVariableValue(vars?.offerId);
}

function resolveOfferReferenceForTaskFetch(task: Task, state: TaskState): string | undefined {
  const fromTask = getTaskOfferReference(task);
  if (fromTask) {
    return fromTask;
  }
  const vars = task.variables as Record<string, unknown> | undefined;
  const fromVars = readTaskVariableValue(vars?.offerReference);
  if (fromVars) {
    return fromVars;
  }
  return state.offer?.reference;
}

const taskModule: Module<TaskState, any> = {
  namespaced: true,
  state: {
    startOrderModule: {
      bind: {}
    },
    validationOrderModule: {
      bind: {}
    },
    module: TaskModule.OFFER,
    analysisModule: {},
    decisionModule: {},
    modifyConditionsModule: {},
    tasks: [],
    tasksToExport:[],
    comment: "",
    selectedTask: {},
    searchTask: [],
    gatheringProcess: [],
    gatheringProcessOrder:[],
    nbrReceivedDocuments: "",
    group: {},
    roles: [],
    groups: [],
    filterByOfferReference: '',
    validateDocumentsModule: {
      gatheringProcessList: []
    },
    printOfferModule: {
      templates: [],
      emailSendList: [],
      body: '',
      showButtonsPreview: false,
      showButtonValidate: false
    },
    members: [],
    actions: [],
    isTaskContinueLoading: false,
    isTaskHydrating: false,
    isTaskQuitLoading:false,
    isClaimActionLoading: false,
    delegationLvlProcess: '',
    updateDelegationLvlProcess: '',
    delegationLvlComment: '',
    isReturnedToAnalysis: false,
    assignReassign: {
      teamMember: '',
      selectedTasks: []
    },
    listKsiopDocuments: []
  },
  getters: {
    getProposalSelected(state) {
      return state.offer?.proposals.filter(proposal => proposal.flagSelected)[0]
    },
    getDelegationLvlComment(state){
      return state.delegationLvlComment
    },
    getFrenchFacilityInit() {
      return {
        _controls: {
          attToRemove: ['isDisabled'],
        },
        facilityType: {
          type: {
            value: '',
          }
        },
        legalId: '',
        activity: {
          from: '',
          until: ''
        },
        name: '',
        address: { ...store.getters["taskModule/getAddressInit"] },
        siret: '',
        objectType: 'odm.party.facility.frenchfacility',
        isDisabled: true
      }
    },
    getFinalDelegationLevel(state){
      return state.selectedTask.variables?._finalDelegationLvl;

    },
    getDelegatedItems(state){
      if(state.selectedTask.variables?._jsonDelegation !== "Error: Variable not present"){
        return JSON.parse(state.selectedTask.variables?._jsonDelegation);
      }
      return [];

    },
    getAddressInit(): Address {
      return {
        type: {
          objectType: 'odm.party.addresstype',
          resourceUid: ''
        },
        line1: '',
        line2: '',
        line3: '',
        territory: '',
        postalCode: {
          type: {
            value: "",
            label: "",
            config: {
              city: "",
              country: { id: "" },
            }
          },
          postalCode: '',
          country: {
            name: '',
            objectType: 'odm.party.country'
          },
          inseeCode: '',
          region: '',
          department: '',
          city: '',
          objectType: 'odm.party.postalcode',
        },

        objectType: 'odm.party.address',
        isDisabled: true,
        daaq: "/"
      }
    },

    isTaskEnterCustomerDecision(state) { return state.selectedTask.taskDefinition === TaskDefinition.ENTER_CUSTOMER_DECISION },
    isTaskCompleteOfferInfo(state) { return state.selectedTask.taskDefinition === TaskDefinition.COMPLETE_OFFER_INFO },
    isTaskUploadDocuments(state) { return state.selectedTask.taskDefinition === TaskDefinition.UPLOAD_DOCUMENTS },
    isTaskSendFinancingRequest(state) { return state.selectedTask.taskDefinition === TaskDefinition.STEP_START_FINANCING_REQUEST },
    isTaskValidateDocuments(state) { return state.selectedTask.taskDefinition === TaskDefinition.VALIDATE_DOCUMENTS },
    isTaskTypeMonoDecision(state) { return state.selectedTask.taskDefinition === TaskDefinition.ENTER_DECISION },
    isTaskPrintDocument(state) { return state.selectedTask.taskDefinition === TaskDefinition.PRINT_DOCUMENT },
    isTaskSignContract(state) { return state.selectedTask.taskDefinition === TaskDefinition.SIGN_CONTRACT },
    isTaskEnterOpinion(state) {
      return Masks.regexOpinion.test(state.selectedTask.taskDefinition as string)
    },
    isTaskAnalysis(state) { return state.selectedTask.taskDefinition === TaskDefinition.ANALYSIS },
    isTaskDelegationLevel(state) { return state.selectedTask.taskDefinition === TaskDefinition.DELEGATION_LEVEL },
    isTaskModifyConditions(state) { return state.selectedTask.taskDefinition === TaskDefinition.MODIFY_CONDITIONS },
    isTaskStartOrder(state) { return state.selectedTask.taskDefinition === TaskDefinition.START_ORDER },
    isTaskCompletePreconditions(state) { return state.selectedTask.taskDefinition === TaskDefinition.COMPLETE_PRECONDITIONS },
    isTaskValidateOrder(state) { return state.selectedTask.taskDefinition === TaskDefinition.VALIDATE_ORDER },
    isTaskOrderCommission(state) { return state.selectedTask.taskDefinition === TaskDefinition.ORDER_COMMISSION },
    isTaskModuleOffer(state) { return state.module === TaskModule.OFFER },
    isTaskModuleOrder(state) { return state.module === TaskModule.ORDER },
    taskDefinitionAllowedToSave() {
      return [
        //To add all necessary have to save an offer
        TaskDefinition.COMPLETE_OFFER_INFO,
        TaskDefinition.ANALYSIS,
        TaskDefinition.MODIFY_CONDITIONS
      ]
    },
    isTaskIsAssignedToConnectedUser(state) {
      const prevState = localStorage.getItem(globalConfig.storageKeys.authModule)
      if (prevState) {
        const connectedUser = JSON.parse(prevState)
        if (state.selectedTask.assignee) {
          const possibleAssignedType1 = `${connectedUser.user.firstName} ${connectedUser.user.lastName}`
          const possibleAssignedType2 = `${connectedUser.user.firstName} ${connectedUser.user.lastName} ## ${connectedUser.user.username}`
          if (state.selectedTask.assignee === possibleAssignedType1 || state.selectedTask.assignee === possibleAssignedType2 || state.selectedTask.assignee.includes(possibleAssignedType1)) {
            return true
          }
        }
      }
      return false
    },
    taskDefinition(state) {
      return state.selectedTask.taskDefinition
    },
    isTaskContinueLoading(state) {
      return state.isTaskContinueLoading
    },
    getIsReturnedToAnalysis(state) {
      return state.isReturnedToAnalysis
    },
    getReserveOpinion() {
      return {
        objectType: "odm.decision.elementreserve.opinionreserve",

        systemUid: "odm-decision",
        businessData: null,
        daaq: "/",
        comment: "",
        reserve: {}
      }
    },
    getReserveDecision() {
      return {
        objectType: "odm.decision.elementreserve.opinionreserve",

        systemUid: "odm-decision",
        businessData: null,
        daaq: "/",
        comment: "",
       statusReserve:{
        objectType:"odm.decision.reservestatus",
        systemUid:"odm-decision",
        resourceUid:"APPROVED"
      },
        reserve: {}
      }
    },
    initOpinion() {
      return {
        objectType: "odm.decision.opinionmaker",
        systemUid: "odm-decision",
        businessData: null,
        daaq: "/",
        opinionMakerId: null,
        comment: "",
        opinion: {
          resourceUid: ""
        },
        reserves: [],
        opinionMaker: "",
        opinionDate: ""
      }
    },
    getOrder(state){
      return state.order;
    },     
    getOffer(state) {
      return state.offer;
    },
    getNbrReceivedDocuments(state){
      return state.nbrReceivedDocuments;
    }
  },
  actions: {

    async updateReserveStatus({ commit }, { reserveId, payload, path, entryIndex}) {
      try {
        // Make the API call to update the reserve status
        const response = await updateReserveStatus(reserveId, payload,path)

        // Commit the mutation to update the local state
        commit('UPDATE_RESERVE_STATUS', {
          reserveId,
          status: payload.statusReserve.resourceUid,
          path: path,
          entryIndex: entryIndex
        });

      } catch (error) {
        logger.error('Failed to update reserve status:', error);
        throw error;
      }
    },


    setModule({ commit }, payload) {
      if (!payload || !payload.module) {
        throw new Error('Module is mandatory!')
      }
      const { module } = payload
      commit("setModule", module)
    },
    getTasks({ commit }, payload) {
      const { offset, limit, taskCriteria, callback, sortBy, sortOrder, isToExport }  = payload;
      getTasks(taskCriteria, offset, limit, sortBy, sortOrder).then((response) => {
        const tasks = response.data;
        if (isToExport) {
          commit("setTasksToExport", tasks);
        } else {
          commit("setTasks", tasks);
        }
        if (!this.state.taskModule.actions.length) {
          const actions = tasks.filter((task: any, index: any, self: any) =>
            index === self.findIndex((t: any) => t.taskDefinition === task.taskDefinition)
          ).map((tk: any) => tk.taskDefinition);
          commit("setActions", actions)
        }
        callback(response)
      });
    },
    setActions({ commit }, payload) {
      commit("setActions", payload)
    },
    setTaskStatus({ commit }, payload) {
      commit("setTaskStatus", payload)
    },
    setTaskOfferStatus({ commit }, payload) {
      if(payload.context === "listener"){
        this.state.taskModule.selectedTask.variables?.offerStatus && (this.state.taskModule.selectedTask.variables.offerStatus = payload.data)
        //this.state.taskModule.offer.status.resourceUid = payload.data
      }else{
        const clone = deepClone(payload.data.variables)
        const variables = JSON.parse(clone)
        const value = (variables._offerStatus ? variables._offerStatus.value : variables.offerStatus.value)
        this.state.taskModule.selectedTask.variables.offerStatus = value
        store.commit('middleOfficeModule/setOfferStatus', value)
      }
     if (payload.context !== "print" && payload.context !== "listener") {
      Notify.create({
        timeout: 10000,
        actions: [{ icon: 'close', color: 'white' }],
        message: i18n.global.t("enum.offer.status." + this.state.taskModule.selectedTask.variables.offerStatus),
        color: "positive",
      });
     }
    },
    
    async executeGetWaitTask(context, payload) {
      const { taskFilter, taskDefinition, onSuccess, onFail } = payload
      if (taskFilter) {
        const tasks = await getWaitTask(taskFilter, taskDefinition)
        if (tasks) {
          logger.debug("success tasks",tasks)
          if (onSuccess) onSuccess(tasks)
        } else {
          logger.debug("failed tasks", tasks)
          if (onFail) onFail()
        }
      }
    },
    async getFutureTaskByReferenceAndTaskDefinition({ dispatch }, payload) {
      const { orderReference, offerReference, taskDefinition, onSuccess, onFail } = payload
      if (!orderReference && !offerReference) {
        throw new Error('At least one reference is required')
      }
      const refKey = orderReference ? 'orderReference' : 'offerReference'
      const refValue = orderReference ? orderReference : offerReference
      return dispatch('executeGetWaitTask', {
        onSuccess,
        onFail,
        taskDefinition,
        taskFilter: {
          searchVariables: JSON.stringify([
            { name: refKey, operator: "eq", value: refValue }
          ])
        }
      })
    },
    setVariablesVisible({ commit }, payload) {
      commit("setVariablesVisible", payload);
    },
    setStatusHistoryVariablesSelected({ commit }, payload) {
      commit("setStatusHistoryVariablesSelected", payload);
    },
    getSelectedTaskOrderDetail({ commit, dispatch }, payload) {
      const loadSeq = payload.loadSeq ?? ++taskDetailLoadSeq;
      const { task } = payload
      const variables = task.variables
      task.variables = parseTaskVariables(variables, task.taskDefinition)
      const orderId = task.variables.orderId
      commit("setModule", TaskModule.ORDER)
      commit("setTask", task)
      return new Promise<void>((resolve) => {
        dispatch('setOrder', {
          orderId,
          callback: () => {
            if (isStaleTaskDetailLoad(loadSeq)) {
              resolve()
              return
            }
            payload.callback?.()
            resolve()
          },
        })
      })
    },
    async resetTaskDecisionContext({ commit, dispatch }) {
      commit("setListDecision", []);
      commit("listReserveOpinion", []);
      await dispatch("demandModule/clearOfferSession", null, { root: true });
      await dispatch("decisionModule/cleanState");
      await dispatch("analysisModule/initAnalysisState");
      await dispatch("analysisModule/initAnalysisState", null, { root: true });
    },
    getSelectedTask({ commit, dispatch, state }, payload) {
      const loadSeq = payload.loadSeq ?? ++taskDetailLoadSeq;
      const { task, origin, taskDefinition } = payload
      const incomingTaskId = task?.taskId;
      const incomingOfferId = getIncomingOfferId(task);
      const incomingOfferRef = getTaskOfferReference(task);
      const demandOfferRef = store.state.demandModule?.offer?.reference;
      const shouldResetDecisionContext =
        (!!incomingTaskId && incomingTaskId !== state.selectedTask?.taskId) ||
        (!!incomingOfferId &&
          !!state.offer?.resourceUid &&
          incomingOfferId !== state.offer.resourceUid) ||
        (!!incomingOfferRef &&
          !!state.offer?.reference &&
          incomingOfferRef !== state.offer.reference) ||
        (!!incomingOfferRef &&
          !!demandOfferRef &&
          incomingOfferRef !== demandOfferRef) ||
        (!!state.decisions?.length &&
          !!incomingOfferId &&
          incomingOfferId !== state.offer?.resourceUid);

      const taskFilter = {
        includeAssignedTasks: true,
        searchVariables: JSON.stringify([{
          name: "offerReference",
          operator: "eq",
          value: resolveOfferReferenceForTaskFetch(task, state),
        }]),
        initializeVariables: TASK_DETAIL_INITIALIZE_VARIABLES
      }

      const fetchTask = () => getTasks(taskFilter).then((response) => {
        if (isStaleTaskDetailLoad(loadSeq)) {
          return
        }
        if (origin && origin === "listener" && response.data[0] && response.data[0].variables) {
          const parsed = JSON.parse(response.data[0].variables);
          const offerStatus = parsed?.offerStatus?.value;
          if(taskDefinition === 'STEP_START_ANALYSIS') {
            dispatch("setTaskOfferStatus", {data: offerStatus, context: origin})
          }
          return
        }
        if (!response.data?.[0]?.variables) {
          return Promise.reject(new Error('Task not found'))
        }
        const variables = JSON.parse(response.data[0].variables)
        task.variables = parseTaskVariables(variables, response.data[0].taskDefinition)
        task.resourceUid = task.variables.decisionProcessId
        if (isStaleTaskDetailLoad(loadSeq)) {
          return
        }
        commit("setTask", task)
        if (task.variables && task.variables.module) {
          commit("setModule", task.variables.module)
        }
        if (task.variables && task.variables.offerId) {
          const offerId = task.variables.offerId
          return new Promise<void>((resolve) => {
            dispatch('setOffer', {
              offerId,
              loadSeq,
              callback: () => {
                if (isStaleTaskDetailLoad(loadSeq)) {
                  resolve()
                  return
                }
                payload.callback?.()
                resolve()
              },
            })
          })
        }
        payload.callback?.()
      })

      const runPrepare = async () => {
        if (origin !== "listener") {
          await dispatch("demandModule/clearOfferSession", null, { root: true });
        }
        if (shouldResetDecisionContext) {
          commit("setGatheringProcess", []);
          await dispatch("resetTaskDecisionContext");
          await dispatch("validateDocumentsModule/cleanState");
        }
      };

      return runPrepare().then(() => fetchTask());
    },
    async hydrateTaskDetail({ dispatch, state, commit }, payload) {
      const loadSeq = ++taskDetailLoadSeq;
      const { taskId, offerRef } = payload

      const sameTask = state.selectedTask?.taskId === taskId
      const offerLoaded =
        state.module === TaskModule.ORDER
          ? !!state.order?.resourceUid
          : !!state.offer?.resourceUid
      const offerMatchesRoute =
        !offerRef || state.offer?.reference === offerRef || state.order?.reference === offerRef

      if (sameTask && offerLoaded && offerMatchesRoute) {
        return
      }

      commit('setTaskHydrating', true)
      store.state.isLoading = true
      commit('setGatheringProcess', [])
      await dispatch('resetTaskDecisionContext')
      await dispatch('validateDocumentsModule/cleanState')
      try {
        if (!offerRef) {
          throw new Error('Missing offerRef')
        }

        const taskFilter = {
          includeAssignedTasks: true,
          searchVariables: JSON.stringify([{
            name: 'offerReference',
            operator: 'eq',
            value: offerRef,
          }]),
          initializeVariables: TASK_DETAIL_INITIALIZE_VARIABLES,
        }
        const response = await getTasks(taskFilter)
        if (isStaleTaskDetailLoad(loadSeq)) {
          return
        }
        const match = response.data?.find((t: Task) => t.taskId === taskId) ?? response.data?.[0]
        if (!match?.variables) {
          throw new Error('Task not found')
        }

        const task: Task = { ...match }
        if (typeof task.variables === 'string') {
          task.variables = JSON.parse(task.variables)
        }

        const moduleValue = (task.variables as Record<string, unknown>)?.module
        const isOrderModule =
          (typeof moduleValue === 'object' && moduleValue !== null &&
            ((moduleValue as { value?: string }).value === 'Order' ||
              (moduleValue as { value?: string }).value === 'Commande')) ||
          moduleValue === 'Order' ||
          moduleValue === 'Commande'

        if (isOrderModule) {
          await dispatch('getSelectedTaskOrderDetail', { task, loadSeq })
        } else {
          await dispatch('getSelectedTask', { task, loadSeq })
        }
      } finally {
        if (!isStaleTaskDetailLoad(loadSeq)) {
          commit('setTaskHydrating', false)
          store.state.isLoading = false
        }
      }
    },
    KsiopDocuments: ({ commit }, payload) => {
      const { request } = payload;
      commit("setKsiopDocuments", request.listKsiopDocuments);
    },
    handleRelations: ({ commit, state }, payload) => {
        const updatedParties = state.offer?.associatedParties?.map((associatedParty: any) => {
            const partyResourceUid = associatedParty.party?.resourceUid;

            const matchedRelation = payload.find(
                (relation: any) =>
                    relation.partyAsc?.resourceUid === partyResourceUid ||
                    relation.partyDesc?.resourceUid === partyResourceUid
            );

            if (!matchedRelation) return associatedParty;

            const updatedRelations = associatedParty.relations?.map((rel: any) => {
                if (rel.resourceUid === matchedRelation.resourceUid) {
                    return {
                        ...rel,
                        relationType: matchedRelation.relationType
                    };
                }
                return rel;
            });

            return {
                ...associatedParty,
                relations: updatedRelations
            };
        });

        commit('setAssociatedParties', updatedParties);
    },
    gatheringProcess: ({ commit }, payload) => {
      const { request } = payload;
      commit("setGatheringProcess", request.gatheringProcess);
    },
    setNbrReceivedDocuments: ({ commit,state }) => {
     const nbrReceivedDoc =  setNbrReceivedDocuments(state.gatheringProcess , state.selectedTask.taskDefinition);
      commit("setNbrReceivedDocuments", nbrReceivedDoc);
    },
    gatheringProcessOrder: ({ commit }, payload) => {
      const { request } = payload;
      commit("setGatheringProcessOrder", request.gatheringProcess);
    },
    getOrderProcess: ({ commit }, payload) => {
      const { request, callback } = payload
      getGatheringProcessFromOrder(request.variable).then(response => {
        if (callback) {
          callback(response)
        }
      })
    },
    getOfferProcess: ({ commit, state }, payload) => {
      const { callback } = payload
      const checkPointsId = state.selectedTask.variables._checkPointsId
      getProcess(checkPointsId).then(response => {
        commit("setGatheringProcess", [response]);
        if (callback) {
          callback([response])
        }
      })
    }
    /*getOfferProcess: ({ commit, state }, payload) => {
      const { request, callback } = payload
      getGatheringProcessFromOffer(request.variable).then(response => {
        if (callback) {
          callback(response)
        }
      })
    }*/,
    getOrderCheckPoints: ({ commit }, payload) => {
      const { request, callback } = payload
      getCheckPointsGathering(request.resourceUid).then(response => {
        if (callback) {
          callback(response)
        }
      })
    },
    updateDocumentValue: ({ commit }, payload) => {
      const { request, callback } = payload
      updateDocumentValue(request).then(response => {
        if (callback) {
          callback(response)
        }
      })
    },
    getDocumentValueConfig: ({ commit }, payload) => {
      const { request, callback } = payload
      getDocumentValueConfig(request).then(response => {
        if (callback) {
          callback(response)
        }else{
          return response
        }
      })
    },
    updateFieldValue: ({ commit }, payload) => {
      const { request, callback } = payload
      updatefieldValue(request).then(response => {
        if (callback) {
          callback(response)
        }
      })
    },

    listDecision({ commit }, payload) {
      const { listDecision, callback } = payload;
      setListDecision(listDecision).then((response: any) => {
        commit("setListDecision", response)
        callback()
      });
    },
    setListDecision({ commit }, payload) {
      commit("setListDecision", payload)
    },
    showDialog: ({ commit }, payload) => {
      const { request } = payload;
      commit("setShowDialog", request.index);
    },
    listReserveOpinion({ commit }, payload) {
      const { reservesOptions } = payload;
      buildReserveOpinion(reservesOptions ?? [])
        .then((response: any) => {
          commit("listReserveOpinion", response);
        })
        .catch((e: any) => {
          logger.error(e);
          commit("listReserveOpinion", []);
        });
    },
    getRoles({ commit }) {
      getRoles()
        .then((res: any) => commit("setRoles", res))
        .catch((e: any) => logger.error(e));
    },
    getGroups({ commit }) {
      const userConnected = this.getters["authModule/getUserConnected"]
      getGroupsByUserId(userConnected.userId)
        .then(res => commit("setGroups", res))
        .catch((e: any) => logger.error(e));
    },
    getMembers({ commit }) {
      const userConnected = this.getters["authModule/getUserConnected"]
      getTeamMembersByManagerCode(userConnected.userId)
        .then(res => commit("setMembers", res))
        .catch((e: any) => logger.error(e));
    },
    setOrder({ commit }, payload) {
      if (!payload || !payload.orderId) {
        throw new Error('orderId is mandatory')
      }
      getOrderEditMode(payload.orderId).then(order => {
        // TODO - EF-161 finish
        commit('setOrder', order)
        if (payload.callback) payload.callback(order)
      })
    },
    saveOrder({ state, dispatch }, payload) {
      if(state.order){
        const order:any =state.order
        
        saveOrder(order)
        .then((res: any) => {
            if (payload && payload.callback) {
                payload.callback(res)
            }
        })
        .catch(e => {
            Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                message: 'ERROR: ' + e,
                color: 'negative'
            });
        })
      }
      
  },
    setOrderUpdate({ commit }, payload) {
      if (!payload || !payload.resourceUid) {
        throw new Error('orderId is mandatory')
      }
        commit('setOrder', payload)

    },
    checkOfferStatus({ commit, dispatch }, payload) {
      const { offerId } = payload
      checkOfferStatus(offerId).then(async (response) => {
        if(response?.offer?.status?.resourceUid === "PAYMENT"){
          router.push('/demand/list')
        }else if(response?.offer?.status?.resourceUid === "ELECSIGNATURE_CANCELLED"){
          dispatch("setTaskOfferStatus", {
            data: "ELECSIGNATURE_CANCELLED",
            context: "listener"
          })
        }
      });
    },
    setOffer({ commit, dispatch }, payload) {
      const { offerId, loadSeq } = payload
      store.dispatch("setSummaryAvailable", false);
      if (!offerId) {
        throw new Error('OfferId is required')
      }
      return getOffer(offerId, this.state, true ).then(async (response) => {
        if (loadSeq !== undefined && isStaleTaskDetailLoad(loadSeq)) {
          return;
        }
        const { offer, offerAssets, proposalScale } = response
        if (!(config.VUE_APP_CALCULATE === 'false' && offer.businessData && offer.businessData.API === businessDataUtils.offerApiSource)) {

          if (this.state.taskModule.selectedTask.taskDefinition === TaskDefinition.ANALYSIS || this.state.taskModule.selectedTask.taskDefinition === TaskDefinition.MODIFY_CONDITIONS) {
           
            store.commit('demandModule/setOffer', offer);
            store.dispatch("setSummaryAvailable", true);
            if (offerAssets && offerAssets.length) {
              store.commit('demandModule/setProposals', offer.proposals)
              store.dispatch('demandModule/updateAssetState', { offerAssets: offerAssets })
              if (proposalScale) {
                store.dispatch('demandModule/setProposalScale', { proposalScale })
              }

              store.dispatch('financingModule/calculateProposalAssetCarAmounts', { skipRecalculateScaleSelected: true })
              store.commit('demandModule/setIsEdit', true)
              store.dispatch('demandModule/setFirstTab', false)
            }

          }
          await getSalesPerson(offer)
          commit('setOffer', offer)
          store.dispatch("setSummaryAvailable", true);
          //dispatch the current context with the offer that was retrieved from the server
          this.dispatch("setCurrentContext", offer);
          //-------------------------------------
     

        }

        else {
          if (this.state.taskModule.selectedTask.taskDefinition === TaskDefinition.ANALYSIS || this.state.taskModule.selectedTask.taskDefinition === TaskDefinition.MODIFY_CONDITIONS) {

            store.commit('middleOfficeModule/setProposalScale', proposalScale)
            await getSalesPerson(offer)
            store.commit('demandModule/setOffer', offer);
            store.commit("middleOfficeModule/setOffer", offer);
            store.dispatch("setSummaryAvailable", true);
            commit('setOffer', offer)
            //dispatch the current context with the offer that was retrieved from the server
            this.dispatch("setCurrentContext", offer);
            if (offerAssets && offerAssets.length) {
              store.commit('demandModule/setProposals', offer.proposals)
              store.dispatch('demandModule/updateAssetState', { offerAssets: offerAssets })
              if (proposalScale) {
                store.dispatch('demandModule/setProposalScale', { proposalScale })
              }

              store.dispatch('financingModule/calculateProposalAssetCarAmounts', { skipRecalculateScaleSelected: true })
              store.commit('demandModule/setIsEdit', true)
              store.dispatch('demandModule/setFirstTab', false)
            }
            store.commit("middleOfficeModule/setshowService", false);
          

          }
          else {
            await getSalesPerson(offer)
            store.commit('demandModule/setOffer', offer);
            store.commit("middleOfficeModule/setOffer", offer);
            store.dispatch("setSummaryAvailable", true);
            commit('setOffer', offer)
            //dispatch the current context with the offer that was retrieved from the server
            this.dispatch("setCurrentContext", offer);
          }

         
        }
       
        const offerInfo = getOfferInfo(offer)
        store.dispatch("demandModule/setPartyInfo", offerInfo)
        store.dispatch("demandModule/setQuoteInfo", offerInfo)
        store.dispatch("demandModule/setSignatureMethod", offer)
        dispatch("prefetchAnalysisTaskResources")
        if (loadSeq !== undefined && isStaleTaskDetailLoad(loadSeq)) {
          return;
        }
        if (payload.callback) payload.callback(offer)
      }).catch(err => {
        logger.error(err)
        Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
          message: err.message,
          color: 'negative'
        });
      })
    },
    prefetchAnalysisTaskResources({ dispatch, state }) {
      const taskDef = state.selectedTask?.taskDefinition;
      const isAnalysisFlow =
        taskDef === TaskDefinition.ANALYSIS ||
        taskDef === TaskDefinition.MODIFY_CONDITIONS;
      if (!isAnalysisFlow) {
        return;
      }

      const documentsPromise = dispatch("validateDocumentsModule/getGatheringProcessList").catch((e) =>
        logger.error(e)
      );

      if (taskDef === TaskDefinition.ANALYSIS) {
        const processId = state.selectedTask?.variables?._decisionAnalysisProcessId;
        if (processId) {
          documentsPromise.then(() => {
            dispatch("analysisModule/analysis", {
              data: processId,
              isEditing: false,
              reCalculate: false,
            }).catch((e) => logger.error(e));
          });
        }
      }
    },
    saveOffer({ commit }) {
      this.dispatch('controlsModule/evaluateActionsControls', {
        onSuccess: () => {
          const offer = this.state.taskModule.offer
          store.state.isLoading = true;
              const associatedParties = offer?.associatedParties ?? [];

              const partiesToValidate = associatedParties.filter((p: any) => {
                const roleCode =  p?.associatedParty?.role?.resourceUid ;
                return roleCode === 'CUSTOMER' || roleCode === 'COLOCATAIRE';
              });

              for (const party of partiesToValidate) {
                const third = party?.associatedParty?.third;
                const typeId = third?.type?.id;

                const isOrganization = typeId === 'party-FrenchOrganization';
                const isPerson = typeId === 'party-FrenchPerson';

                if (!isOrganization && !isPerson) continue;

                const mandatoryValidation = validateOrganizationMandatoryFields(third, isOrganization, {
                  requireQualificationType: true,
                  requireTvaIntracom: isOrganization,
                });
                const role = partiesToValidate.indexOf(party) === 0 ? i18n.global.t("demand.party.roles.CUSTOMER") : i18n.global.t("demand.party.roles.COLOCATAIRE");
                if (!mandatoryValidation.isValid) {
                  Notify.create({
                    timeout: 10000,
                    actions: [{ icon: 'close', color: 'white' }],
                    message: `${role} : ${mandatoryValidation.fieldLabel} - ${i18n.global.t("main.messageRequiredField")}`,
                    color: 'negative',
                  });
                  store.state.isLoading = false;
                  return;
                }
              }
            saveOfferFromTask(offer)
              .then(response => {
                commit('setOffer', response)
                store.dispatch("summaryPanelModule/setOfferFromTask", { offer: response });
                store.state.demandModule.offer = response
                store.dispatch("setSummaryAvailable", true);
                store.state.isLoading = false;
                Notify.create({
                  timeout: 10000,
                  actions: [{ icon: 'close', color: 'white' }],
                  message: i18n.global.t('main.dialog.saveSuccess', { offer: store.state.demandModule.offer?.reference }),
                  color: "positive",
                });
                if (this.getters["taskModule/isTaskAnalysis"]) {
                  store.dispatch("analysisModule/updateAnalysis").then(()=>{
                      setTimeout(() => {
                        Notify.create({
                          timeout: 10000,
                          actions: [{ icon: 'close', color: 'white' }],
                          message: i18n.global.t("decision.analysisTab.successMessage"),
                          color: "positive",
                        })}, 2000);
                    
                  })
                }
              })
              .catch(err => {
                store.state.isLoading = false;
                logger.error(err)
                Notify.create({
                  timeout: 10000,
                  actions: [{ icon: 'close', color: 'white' }],
                  message: err.message || err.error || err,
                  color: 'negative'
                });
              })
         
        
        },
        onFail: (message: string) => {
          store.state.isLoading = false;
          Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
            message,
            color: "warning",
          });
        },
        entity: 'Offer'
      })
    },
    async executeOrderAction({ commit, dispatch, state }, payload) {
      const { selectedTask } = state
      const { actionType } = payload
      if (!actionType) {
        throw new Error('ActionType is mandatory!')
      }
      const userConnected = this.getters["authModule/getUserConnected"]
      executeOrderAction(this.state.taskModule, userConnected, actionType)
        .then(response => {
          if (payload && payload.callback) payload.callback(response)
          if (response) commit('setExecutedActionEvent', response)
        })
        .catch(err => {
          logger.error(err)
          Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
            message: err.message || err.error || err,
            color: 'negative'
          });
        })
    },
    async executeOrderActionStartOrder({ commit, dispatch, state }, payload) {
      const { selectedTask } = state
      const { actionType } = payload
      if (!actionType) {
        throw new Error('ActionType is mandatory!')
      }
      const userConnected = this.getters["authModule/getUserConnected"]
      if(state.order){
        const order:any =state.order
        
            saveOrder(order)
            .then((res: any) => {
              commit('setOrder', res)
          executeOrderAction(this.state.taskModule, userConnected, actionType)
            .then(response => {
              if (payload && payload.callback) payload.callback(response)
              if (response) commit('setExecutedActionEvent', response)
            })
            .catch(err => {
              logger.error(err)
              Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                message: err.message || err.error || err,
                color: 'negative'
              });
            })

            })
            .catch(e => {
                Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                    message: 'ERROR: ' + e,
                    color: 'negative'
                });
            })
      }
      
    }
    ,
    async executeAction({ commit, dispatch }, payload) {
      const { actionType, currentTask } = payload
      if (!actionType) {
        throw new Error('ActionType is mandatory!')
      }
      if (actionType == ActionType.DOWNLOAD_DOCUMENT) {
        commit('setShowLoadingDownload', true)
      }
      if (actionType == ActionType.SEND_DOCUMENT) {
        const selectedCount = getSelectedDocumentsCountForSend();
        if (selectedCount > MAX_TRANSMIT_DOCUMENTS) {
          Notify.create({
            timeout: 8000,
            actions: [{ icon: "close", color: "white" }],
            message: i18n.global.t("task.printDocument.maxTransmitDocuments", {
              max: MAX_TRANSMIT_DOCUMENTS,
            }),
            color: "negative",
          });
          store.state.isLoading = false;
          return;
        }
        commit('setShowSendTemplate', true)
        SendAllDocuments()
        store.state.isLoading = false;
        return
      }
      const userConnected = this.getters["authModule/getUserConnected"]
      await this.dispatch('controlsModule/evaluateActionsControls', {
        onSuccess: () => executeAction(this.state.taskModule, userConnected, actionType)
          .then(response => {
            const clonedTask = this.state.taskModule.selectedTask
      
            if (actionType == ActionType.DOWNLOAD_DOCUMENT) {
              DownloadAllDocuments()
              // commit('checkDownload', true)
            }
            Notify.create({
              timeout: 5000,
              actions: [{ icon: 'close', color: 'white' }],
              message: i18n.global.t('task.labels.actionValidationSuccess', { actionType: i18n.global.t(`enum.offer.ClientTaskDefinition.${clonedTask.taskDefinition}`) }),
              color: "positive",
            });
            if (payload && payload.callback) payload.callback(response)
            if (response) commit('setExecutedAction', actionType)
              if (this.getters["taskModule/isTaskPrintDocument"]) {
                const assets = this.state.taskModule.offer?.proposals?.[0]?.proposalItems?.[0]?.proposalAssets;
                if (assets) {
                  assets.forEach((asset:any) => {
                    if (asset.config?.productAssetModel?.config?.inventoryItems?.length > 0) {
                      const inventoryItems = asset.config.productAssetModel.config.inventoryItems;
                      inventoryItems.forEach((item:any) => {
                        store.dispatch("orderModule/updateInventoryItem", {
                          inventoryItemCode: item.resourceUid,
                          status: InventoryStatusEnum.SOLD
                        });
                      });
                    }
                  });
                }
              }
            if (this.state.taskModule.selectedTask?.proceed) {
              commit('setTaskContinueLoading', true)
              store.state.isLoading = true
              const clonedTask =  this.state.taskModule.selectedTask
              store.dispatch('taskModule/getFutureTaskByReferenceAndTaskDefinition', {
                offerReference: this.state.taskModule.offer.reference,
                taskDefinition: this.state.taskModule.selectedTask.taskDefinition,
                onSuccess: async (task: Task) => {
                  const variables = task.variables
                  task.variables = parseTaskVariables(variables, task.taskDefinition)
                  await dispatch('claimOwnership', {
                    task: task,
                    actionType: clonedTask.taskName
                  })
                  .then(() => {
                    store.state.isLoading = false
                    commit('setTaskContinueLoading', false)

                  })
                },
                onFail: async () => {
                  const offerResourceUid =
                    this.state.taskModule.offer?.resourceUid;
                  const externalReference =
                    this.state.taskModule.offer?.externalReference;

                  commit('setProceed', false)
                  store.dispatch("taskModule/cleanState", {
                    isOrder: false
                  });
                  store.state.isLoading = false
                  commit('setTaskContinueLoading', false)

                  if (offerResourceUid) {
                    try {
                      const { offer } = await checkOfferStatus(offerResourceUid);
                      if (offer?.status?.resourceUid === "REFUSED") {
                        Notify.create({
                          timeout: 10000,
                          actions: [{ icon: 'close', color: 'white' }],
                          message: i18n.global.t(
                            "task.labels.offerDecisionRefusedNotify",
                            { reference: externalReference
                          }),
                          color: "warning",
                        });
                      }
                    } catch (e) {
                      logger.error(e);
                    }
                  }

                  router.push("/task/list");
                }
              })
            }
          })
          .catch(err => {
            logger.error(err)
            Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
              message: err.message || err.error || err,
              color: 'negative'
            });
          }),
        onFail: (message: string) => {
          store.state.isLoading = false
          commit('setTaskContinueLoading', false)
          Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
            message,
            color: "warning",
          });
        },
        entity: currentTask
      })
    },
    claimOwnership({ commit, dispatch, getters, state }, payload) {
      if (state.isClaimActionLoading) {
        return Promise.resolve()
      }
      store.state.isLoading = true
      commit('setClaimActionLoading', true)
      const { task, callback, blockRedirection, actionType } = payload
      const userConnected = this.getters["authModule/getUserConnected"]
      const userId = `${userConnected.firstName} ${userConnected.lastName} ## ${userConnected.username}`
      logger.debug("task variables", task.variables)
      const taskDefinition = task.taskDefinition
      logger.debug("task def from taskModule", taskDefinition)
      const businessKey = task.variables.offerId.value || task.variables.offerId 
      return takeChargeTask(task.taskId, userId, businessKey, taskDefinition).then(() => {
        task.assignee = `${userConnected.firstName} ${userConnected.lastName}`
        commit('setTaskAssignedUser', task)
        Notify.create({
          timeout: 5000,
          actions: [{ icon: 'close', color: 'white' }],
          message: i18n.global.t("task.buttons.claim"),
          color: 'positive'
        })
        task.isLoading = true;
        if (blockRedirection) {
          const refreshOffer = getters.isTaskModuleOffer && businessKey
            ? dispatch('setOffer', { offerId: businessKey })
            : Promise.resolve()
          return Promise.resolve(refreshOffer).finally(() => {
            task.isLoading = false
            commit('setClaimActionLoading', false)
            store.state.isLoading = false
            if (callback) callback()
          })
        }
        else {
        
          if (getters.isTaskModuleOffer) {
            dispatch("getSelectedTask", {
              task,
              callback: () => {
                if (callback) callback()
                if (actionType) {
             
                  store.commit("setLockState", false);
                  store.state.lockState = false;
                  store.commit("setConfirmationDialogState", {
                    show: false,
                  });
                }
                navigateToTaskDetail(task)
                  .then(() => (task.isLoading = false,
                    store.state.lockState = false));
                commit('setClaimActionLoading', false)
                store.state.isLoading = false
              },
            });
          } else {
            dispatch("getSelectedTaskOrderDetail", {
              task,
              callback: () => {
                navigateToTaskDetail(task).then(() => {
                  task.isLoading = false
                  commit('setClaimActionLoading', false)
                  store.state.isLoading = false
                });
              },
            });
          }
        }
       
      }).catch(err => {
        logger.error(err)
        Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
          message: i18n.global.t("main.register.claimedTask"),
          color: 'negative'
        })
        commit('setClaimActionLoading', false)
        store.state.isLoading = false
      }
      )


    },
    checkForm({ commit }, payload) {
      const { valid } = payload
      commit('setcheckForm', !valid)
    },
    unclaimOwnership({ commit, dispatch, state }, payload) {
      if (state.isClaimActionLoading) {
        return Promise.resolve()
      }
      store.state.isLoading = true
      commit('setClaimActionLoading', true)
      const { task, callback, blockRedirection, actionType } = payload
      const userConnected = this.getters["authModule/getUserConnected"]
      const userId = `${userConnected.firstName} ${userConnected.lastName} ## ${userConnected.username}`
      task.assignee = null
      logger.debug("task variables", task.variables)
      const taskDefinition = task.taskDefinition
      logger.debug("task def from taskModule", taskDefinition)
      const businessKey = task.variables.offerId.value || task.variables.offerId 
      commit('setTaskAssignedUser', task)
      return abandonTask(task.taskId, userId, businessKey, taskDefinition).then(() => {
        Notify.create({
          timeout: 5000,
          actions: [{ icon: 'close', color: 'white' }],
          message: i18n.global.t("task.buttons.unclaim"),
          color: 'positive'
        })
        if (blockRedirection) {
          task.isLoading = false
          commit('setClaimActionLoading', false)
          store.state.isLoading = false
          if (callback) callback()
          return
        }

        dispatch("getSelectedTask", {
          task,
          callback: () => {
            if (callback) callback()
            if (actionType) {
              store.commit("setLockState", false);
              store.state.lockState = false;
              store.commit("setConfirmationDialogState", {
                show: false,
              });
            }
            navigateToTaskDetail(task)
              .then(() => (task.isLoading = false,
                store.state.lockState = false));
            commit('setClaimActionLoading', false)
            store.state.isLoading = false
          },
        });

      }).catch(err => {
        logger.error(err)
        Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
          message: err.message || err.error || err,
          color: 'negative'
        });
        commit('setClaimActionLoading', false)
        store.state.isLoading = false
      })
    },
    setFilterByOfferReference({ commit }, payload) {
      commit('setFilterByOfferReference', payload)
    },
    setFormValid({ commit }, payload) {
      const { valid } = payload
      commit('setFormValid', !!valid)
    },
    setIsFormValid({ commit }, payload) {
      const { valid } = payload
      commit('setIsFormValid', !!valid)
    },
    updateOffer({ commit }, payload) {
      const { offer } = payload
      if (offer) {
        commit('setOffer', offer)
      }
    },
    cleanState({ commit, dispatch }, payload) {
      const isOrder = payload?.isOrder ?? false;
      dispatch('decisionModule/cleanState')
      dispatch('printOfferModule/cleanState')
      dispatch('partyModule/cleanState')
      dispatch('startOrderModule/cleanState')
      dispatch('validationOrderModule/cleanState')
      if (!isOrder) {
        this.dispatch('summaryPanelModule/cleanState')
      }
      dispatch('validateDocumentsModule/cleanState')
      dispatch('modifyConditionsModule/cleanState')
      dispatch('printDocumentModule/cleanState')
      dispatch('analysisModule/initAnalysisState')
      dispatch('analysisModule/initAnalysisState', null, { root: true })

      commit('setInitial', isOrder)
    },
    async clearTaskSession({ dispatch }) {
      await dispatch("resetTaskDecisionContext");
      await dispatch("cleanState", { isOrder: false });
    },
    assignTask(actionContext: ActionContext<TaskState, unknown>, payload) {
      const { memberCode, taskInfos, callback } = payload;
      taskInfos.forEach(async (task: { taskId: string, assignee: string, variables: { offerReference: { value: string } } }) => {
        const { taskId, assignee, variables } = task;
        if (assignee) {
          const taskFilter = {
            searchVariables: JSON.stringify([{
              name: "offerReference",
              operator: "eq",
              value: variables.offerReference.value || '',
            }]),
            initializeVariables: taskVariables.offer,
            includeAssignedTasks: true
          }
          getTasks(taskFilter).then(async res => {
            if (assignee === res.data[0].assignee) {
              await unClaimTask({ userId: assignee, taskId }).catch((e: Error) => logger.error(e));
              await assignTask({ userId: memberCode, taskId }).catch((e: Error) => logger.error(e));
              callback();
            }
          })
        }
        else {
          await assignTask({ userId: memberCode, taskId }).catch((e: Error) => logger.error(e));
          callback();
        }
      })
    },
    setTaskContinueLoading({ commit }, payload) {
      commit('setTaskContinueLoading', payload)
    },
    setTaskQuitLoading({ commit }, payload) {
      commit('setTaskQuitLoading', payload)
    },
    updateCheckPointsStatus: ({ commit }, payload) => {
      const { request } = payload
      updateCheckPointsStatus(request).then().catch((e: Error) => logger.error(e))
    },
    setAssociatedParties({ commit }, payload) {
      commit('setAssociatedParties',payload)
    },
async sendMessage({ commit }, payload) {
  const { messageTosend, module, target, callback } = payload;

  try {
    const res: any = await sendMessage(messageTosend);

    if (target && !callback) {
      if (module === "Offer") {
        const variables = JSON.parse(res.data[0].variables);
        this.state.taskModule.selectedTask.variables.offerStatus =
          variables.offerStatus.value;

        store.commit("setLockState", false);
        store.commit("setConfirmationDialogState", { show: false });

        router.push(target);
      }
      else if (module === "Order") {
        await store.dispatch("releaseLockState");
        router.push(target);
      }
    }

    if (callback) {
      await callback(res);
    }

  } catch (e) {
    Notify.create({
      timeout: 5000,
      actions: [{ icon: 'close', color: 'white' }],
      message: `Erreur au niveau de l'execution de l'action`,
      color: "negative",
    });

    logger.error(e);
  }
},
    saveCheckPointsValue: ({ getters }, payload) => {
      const { request } = payload
      const { objectType, resourceUid, fieldValue } = request
      if (getters.isTaskModuleOrder){
        const orderInfo = JSON.stringify(getters.getOrder);
        const checkPointData = {
          objectType,
          resourceUid,
          fieldValue,
          orderInfo
        }
        saveCheckPointsValue(checkPointData).catch(() =>
          Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
          message: 'Save Checkpoint value error',
          color: 'negative'
        }));
      } 
    },
    setShowLoadingDownload({ commit },payload) {
      commit("setShowLoadingDownload",payload)
    },
    removeAccountFromParty({ commit, state, rootGetters }, { partyUid, index, accountUid }) {
      commit('removeAccountFromParty', { partyUid, index, accountUid });
    },
    setUpdatedDelegationLvlProcess({ commit }, payload) {
      commit('setUpdateDelegationLevelProcess', payload);
    },
    checkPreferredAccountsWithoutIban({ state }): boolean {
      const aps = state.offer?.associatedParties ?? [];
      return aps.some(ap =>
          (ap?.associatedParty?.third?.accounts ?? []).some(acc => {
            const preferred = !!acc?.bankAccount?.isPreferred;
            const iban = acc?.bankAccount?.iban;
            return preferred && (!iban || String(iban).trim() === '');
          })
      );
    },
  },
  mutations: {
    updateReceivedFileUid(state, payload) {
      const { documentType, reference, receivedFileUid } = payload;
      state.gatheringProcess.forEach((process: any) => {
        process.entityTargets.forEach((target: any) => {
          target.documents.forEach((doc: any) => {
            if (doc.documentType === documentType && doc.reference === reference) {
              doc.receivedFileUid = receivedFileUid;
            }
          });
        });
      });
    },
    UPDATE_RESERVE_STATUS(state, { reserveId, status, path, entryIndex }) {
      const stateToFindIn = path === Paths.analysisreserve ? state.decisions[0].associatedAnalysisProcess[0].reserves : state.decisions[0].opinions[entryIndex].reserves 
      // Find the reserve in the state that matches the given reserveId
      const reserveIndex = stateToFindIn.findIndex(
        (reserve:any) => reserve.resourceUid === reserveId
      );

      // If the reserve is found, update its status
      if (reserveIndex !== -1) {
        // Create a new object to maintain reactivity
        const updatedReserve = {
          ...stateToFindIn[reserveIndex],
          statusReserve: {
            objectType: UON.ObjectType.odmDecisionReserveStatus,
            systemUid: UON.SystemUid.odmDecision,
            resourceUid: status
          }
        };

        // Use Vue's reactivity method to update the array
        stateToFindIn.splice(reserveIndex, 1, updatedReserve);
      }
    },
      SET_GATHERING_PROCESS(state: any, payload: any) {
        state.gatheringProcess = payload;
    },
      setDelegationLVLComment(state: any, payload: any) {
        state.delegationLVLComment = payload;
    },
    setInitial(state, payload) {
      state.tasks = []
      state.tasksToExport = []
      state.selectedTask = {}
      state.searchTask = []
      state.gatheringProcess = []
      state.gatheringProcessOrder = []
      state.group = {}
      state.roles = []
      state.actions = []
      state.members = []
      state.groups = []
      state.filterByOfferReference = ''
      state.comment = ''
      state.assignReassign = {
        teamMember: '',
        selectedTasks: []
      }
      state.isTaskContinueLoading = false
      state.isTaskHydrating = false
      state.isTaskQuitLoading = false
      state.isClaimActionLoading = false
      state.isReturnedToAnalysis = false
      delete state.offer
      delete state.order
      delete state.taskDefinition
      delete state.executedAction
      delete state.executedActionEvent
      delete state.decisions
      state.delegationLvlProcess = ''
      state.updateDelegationLvlProcess = ''
      state.delegationLvlComment = ''
      state.module = payload ? TaskModule.ORDER : TaskModule.OFFER
      state.listKsiopDocuments = []
      state.Openionreserves = []
    },
    setModule(state, payload) {
      state.module = payload;
    },
    setOrder(state, payload) {
      state.order = payload
    },
    setExecutedAction(state, payload) {
      state.executedAction = payload;
    },
    setExecutedActionEvent(state, payload) {
      state.executedActionEvent = payload;
    },
    setFormValid(state, payload) {
      state.formValid = payload
    },
    setIsFormValid(state, payload) {
      state.isFormValid = payload
    },
    setShowSendTemplate(state, payload) {
      state.printOfferModule.showSendTemplate = payload
    },
    setShowLoadingDownload(state, payload) {
      state.printOfferModule.showLoadingDownload = payload
    },
    setOffer(state, payload) {
      state.offer = payload
    },
    setListDecision(state, payload) {
      state.decisions = payload
    },
    listReserveOpinion(state, payload) {
      state.Openionreserves = payload
    },
    setcheckForm(state, payload) {
      state.checkForm = payload
    },
    setIsReturnedToAnalysis(state, payload) {
      state.isReturnedToAnalysis = payload
    },
    setTasks(state, payload) {
      state.tasks = []
      const tasks = payload.map((task: Task) => {
        if (task.variables && typeof task.variables === "string") {
          task.variables = JSON.parse(task.variables);
        }
        return task;
      });
      state.tasks = deepClone(tasks)
    },
    setTasksToExport(state, payload) {
      state.tasksToExport = []
      const tasks = payload.map((task: Task) => {
        if (task.variables && typeof task.variables === "string") {
          task.variables = JSON.parse(task.variables);
        }
        return task;
      });
      state.tasksToExport = deepClone(tasks)
    },
    setTask(state, payload) {
      state.selectedTask = payload;
    },
    setGatheringProcess(state, payload) {
      state.gatheringProcess = payload;
    },
    setKsiopDocuments(state, payload) {
      state.listKsiopDocuments = payload;
    },

    setGatheringProcessOrder(state, payload) {
      state.gatheringProcessOrder = payload;
    },
    setNbrReceivedDocuments(state,payload){
      state.nbrReceivedDocuments = payload
    },
    setRoles(state, payload) {
      state.roles = payload;
    },
    setGroups(state, payload) {
      state.groups = payload;
    },
    setShowDialog(state, payload) {
      state.gatheringProcess[payload].showDialog =
        !state.gatheringProcess[payload].showDialog;
    },
    setFilterByOfferReference(state, payload) {
      state.filterByOfferReference = payload;
    },
    checkDownload(state, payload) {
      state.printOfferModule.showButtonValidate = payload
    },
    setTaskAssignedUser(state, payload) {
      state.selectedTask = payload
    },
    setMembers(state, payload) {
      state.members = payload;
    },
    setActions(state, payload) {
      state.actions = payload
    },
    setTaskContinueLoading(state, payload) {
     
      state.isTaskContinueLoading = payload
    },
    setTaskQuitLoading(state, payload) {
      state.isTaskQuitLoading = payload
    },
    setTaskHydrating(state, payload) {
      state.isTaskHydrating = payload
    },
    setClaimActionLoading(state, payload) {
      state.isClaimActionLoading = payload
    },
    setProceed(state, payload) {
      state.selectedTask.proceed = payload
    },
    setShowPopupDialog(state, payload) {
      state.selectedTask.showPopupDialog = payload
    },
    setSelectedAction(state, payload) {
      state.selectedTask.selectedAction = payload
    },
    setTeamMember(state, payload) {
      state.assignReassign.teamMember = payload
    },
    setTaskStatus(state, payload) {
      state.selectedTask.variables.offerStatus = payload
    },
    setAssociatedParties(state, payload) {
      state.selectedTask.associatedParties = payload
    },
    setVariablesVisible(state, payload) {
      state.variablesVisible = payload;
    },
    setStatusHistoryVariablesSelected(state, payload) {
      state.statusHistoryVariableSelected = payload;
    },
    setCurrentOpinion(state, payload) {
      if(state.decisions && state.decisions.length > 0){
        let missingOpinion 
        if (state.decisions[state.decisions.length -1].opinions 
        && state.decisions[state.decisions.length -1].opinions.length > 0){
          missingOpinion =  state.decisions[state.decisions.length -1].opinions.every((item: any) => item.opinionMakerId?.resourceUid);;
        }
        if(!state.decisions[state.decisions.length -1].opinions || (state.decisions[state.decisions.length -1].opinions 
          && state.decisions[state.decisions.length -1].opinions.length === 0)){
            missingOpinion = true;
          }
         if(missingOpinion) {      
            state.decisions[state.decisions.length -1].opinions.push(payload);
          }
      }else {
        return true
      }
    },
    addAccountToParty(state, { partyUid, account }) {
      const ap = state.offer?.associatedParties?.find(
          (p: any) => p?.associatedParty?.third?.resourceUid === partyUid
      );
      if (!ap) return;

      if (!ap.associatedParty) ap.associatedParty = {};
      if (!ap.associatedParty.third) ap.associatedParty.third = {};
      if (!Array.isArray(ap.associatedParty.third.accounts)) {
        ap.associatedParty.third.accounts = [];
      }

      if(ap.associatedParty.third.accounts.length === 0){
        account.bankAccount.isPreferred = false;
      }
      ap.associatedParty.third.accounts.push(account);
    },
    removeAccountFromParty(state, { partyUid, index, accountUid }) {
      const ap = state.offer?.associatedParties?.find(
          (p) => p?.associatedParty?.third?.resourceUid === partyUid
      );
      if (!ap) return;

      if (!ap.associatedParty) ap.associatedParty = {};
      if (!ap.associatedParty.third) ap.associatedParty.third = {};
      if (!Array.isArray(ap.associatedParty.third.accounts)) {
        ap.associatedParty.third.accounts = [];
      }

      const accounts = ap.associatedParty.third.accounts;

      let idx = -1;
      if (typeof index === 'number') {
        idx = index;
      } else if (accountUid) {
        idx = accounts.findIndex(
            (a) =>
                a?.resourceUid === accountUid ||
                a?.bankAccount?.resourceUid === accountUid
        );
      }
      if (idx < 0 || idx >= accounts.length) return;

      const removing = accounts[idx];
      const removingBankId = removing?.bankAccount?.resourceUid;
      const currentPrefId = ap.partyAccount?.resourceUid;

      const isRemovingPreferred =
          (removingBankId && currentPrefId === removingBankId) ||
          (!removingBankId &&
              (currentPrefId === undefined || currentPrefId === null) &&
              !!removing?.bankAccount?.isPreferred);

      accounts.splice(idx, 1);

      if (isRemovingPreferred) {
        if (!ap.partyAccount) ap.partyAccount = {};
        ap.partyAccount.resourceUid = undefined;

        accounts.forEach((acc) => {
          if (Object.prototype.hasOwnProperty.call(acc?.bankAccount ?? {}, 'isPreferred')) {
            acc.bankAccount.isPreferred = false;
            acc.bankAccount.businessData.preleved = 0;
          }
        });
      }
    },
    ensurePartyHasAtLeastOneAccount(state, { partyUid, emptyAccount }) {
      const ap = state.offer?.associatedParties?.find(
          (p: any) => p?.associatedParty?.third?.resourceUid === partyUid
      );
      if (!ap) return;

      if (!ap.associatedParty) ap.associatedParty = {};
      if (!ap.associatedParty.third) ap.associatedParty.third = {};
      if (!Array.isArray(ap.associatedParty.third.accounts)) {
        ap.associatedParty.third.accounts = [];
      }

      if (ap.associatedParty.third.accounts.length === 0) {
        emptyAccount.bankAccount.isPreferred = true;
        ap.associatedParty.third.accounts.push(emptyAccount);
      }
    },
    setAssociatedPartyAccountPreferred(state, payload: { partyUid: string; resourceUid: string }) {
      const assoc = state.offer?.associatedParties?.find((p: any) => p.uid === payload.partyUid);
      if (!assoc) return;
      assoc.partyAccount = { resourceUid: payload.resourceUid };
    },
    setUpdateDelegationLevelProcess(state, payload) {
      state.updateDelegationLvlProcess = payload;
    },
    setSignatureMethod(state, payload){
      state.selectedTask.variables.signatureMethod = payload;
    }
  },
  modules: {
    decisionModule,
    uploadDocumentModule,
    partyModule,
    financingRequestModule,
    validateDocumentsModule,
    printOfferModule,
    analysisModule,
    modifyConditionsModule,
    startOrderModule,
    validationOrderModule,
    printDocumentModule
  },
};

export default taskModule;
